import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Checkbox from '../../components/Checkbox'
import Input from '../../components/Input'
import { loginInitialValues, LoginSchemaType, loginValidationSchema } from '../../forms/login'
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik'
import useApi from '../../hooks/use-api';
import { AppRoutes } from '../../utils/props';
import { OutlinedError } from '../../icons';

export default function LoginPage() {
  const { t } = useTranslation('ns');
  const navigate = useNavigate();
  const form = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginValidationSchema,
    onSubmit: (values) => {
      onLogin(values)
    },
  })
  
  const { errors, touched, getFieldProps, handleSubmit } = form

  const login = useApi({
    path: '/login',
    defaultErrorMessage: t('login-incorrect-data'),
    callback: (data) => {
      localStorage.setItem('token', JSON.stringify(data.auth_token));
      localStorage.setItem('refresh_token', JSON.stringify(data.refresh_token));
      // After login, fetch user data
      user.get().then(() => {
        if(localStorage.getItem('type') === '2'){
          // If regular user, redirect to personal information
          navigate(AppRoutes.PersonalInformation);
        }else if (localStorage.getItem('type') === '3') {
          // If admin, redirect to users page
          navigate(AppRoutes.Users);
        }
      });
    },
    errorCallback: (error) => {
      form.setErrors({email: errors.email || error?.response?.data?.message || '', password: errors.password})
    }
  });

  const user = useApi({
    path: '/user',
    callback: (data) => {
      localStorage.setItem('user_id', JSON.stringify(data.id));
      localStorage.setItem('first_name', JSON.stringify(data.first_name));
      localStorage.setItem('last_name', JSON.stringify(data.last_name));
      localStorage.setItem('company_name', JSON.stringify(data.company_name));
      localStorage.setItem('phone', JSON.stringify(data.phone));
      localStorage.setItem('mail', JSON.stringify(data.mail));
      localStorage.setItem('date_of_birth', JSON.stringify(data.date_of_birth));
      localStorage.setItem('type', JSON.stringify(data.type));
    }
  })

  const onLogin = async (
    values: LoginSchemaType
  ) => {
    const { email, password, rememberMe } = values
    localStorage.setItem('remember_me', JSON.stringify(rememberMe));
    await login.post({
      mail: email,
      key: password,
      auth_method: 0,
    })
  }


  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-cover bg-center"
      style={{
        background: `linear-gradient(180deg, #086CE1 0%, rgba(8, 108, 225, 0) 98%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 98%), url('/images/account-background.jpg') lightgray -2.651px -381.993px/100.239% 142.326% no-repeat`,
      }}
    >
      <div className="relative z-10 flex w-full flex-col items-center">
        <h1 className="mx-auto mb-12 text-white max-w-lg text-center text-3xl font-extrabold text-primary drop-shadow-lg md:text-4xl">
          {t('loginTitle', { defaultValue: 'Login to Your Account' })}
        </h1>
        <div className="w-full max-w-md px-4">
          {form.status && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              <OutlinedError
                width={20}
                height={20}
                hardcodedColor="#DC2626"
                className="mt-0.5 shrink-0"
              />
              <span>{form.status}</span>
            </div>
          )}
          <form className="flex flex-col items-center px-8 pb-8 pt-10" onSubmit={handleSubmit}>
            <div className="mb-6 w-full">
              <Input
                {...getFieldProps('email')}
                title={t('email')}
                type="email"
                error={touched.email ? (errors.email ?? '') : ''}
                labelClassName="text-white"
              />
            </div>
            <div className="mb-2 w-full">
              <Input
                {...getFieldProps('password')}
                title={t('password')}
                type="password"
                error={touched.password ? (errors.password ?? '') : ''}
                labelClassName="text-white"
              />
            </div>
            <div className="mb-4 flex w-full justify-end">
              <Link
                className="bg-transparent text-xs font-medium text-blue-100 transition hover:underline"
                to={AppRoutes.ForgotPassword}
              >
                {t('forgotPassword')}
              </Link>
            </div>
            <div className="mb-6 flex w-full items-center">
              <Checkbox
                label={t('rememberMe')}
                {...getFieldProps('rememberMe')}
                labelClassName="text-white"
              />
            </div>
            <button
              className="mb-6 w-full rounded bg-blue-600 py-3 text-lg font-bold text-white shadow-md transition hover:bg-blue-700"
              type="submit"
            >
              {t('login')}
            </button>
            <div className="mt-2 w-full text-center text-base text-white">
              <span>{t('noAccount', { defaultValue: "Don't have an account? " })}</span>
              <Link
                className="font-semibold text-blue-700 underline transition hover:text-white hover:underline"
                to={AppRoutes.Register}
              >
                {t('registerHere')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}